import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, PackageCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { receiveOrder } from '../services/orders.services';

const ReceiveOrderModal = ({ open, onOpenChange, order, onReceived }) => {
  const { toast } = useToast();

  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [numeroFactura, setNumeroFactura] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Items con cantidad recibida editable
  const [receivedItems, setReceivedItems] = useState([]);

  // Inicializar items cuando se abre el modal con un pedido
  useEffect(() => {
    if (open && order) {
      const items = (order.items || []).map((it, idx) => {
        const cant = Number(it?.cantidad ?? 0);
        const litros = Number(it?.litrosTotal ?? 0);
        const pres = cant > 0 ? litros / cant : 0;
        return {
          idx,
          productoId: it?.producto?.id || it?.producto?._id || '',
          productoNombre: it?.producto?.nombre || '',
          cantidadSolicitada: cant,
          litrosSolicitados: litros,
          presentacionLitros: pres,
          costoUnitario: Number(it?.costoUnitario ?? 0),
          cantidadRecibida: String(cant), // Pre-llenar con la cantidad solicitada
        };
      });
      setReceivedItems(items);
      setFecha(new Date().toISOString().slice(0, 10));
      setNumeroFactura('');
      setObservaciones('');
      setSubmitting(false);
    }
  }, [open, order]);

  const updateReceivedQty = (idx, value) => {
    setReceivedItems(prev =>
      prev.map((it, i) => (i === idx ? { ...it, cantidadRecibida: value } : it))
    );
  };

  const updateCosto = (idx, value) => {
    setReceivedItems(prev =>
      prev.map((it, i) => (i === idx ? { ...it, costoUnitario: value } : it))
    );
  };

  // Verificación de cantidades
  const itemsWithStatus = useMemo(() => {
    return receivedItems.map(it => {
      const recibida = Number(it.cantidadRecibida) || 0;
      const solicitada = it.cantidadSolicitada;
      const litrosRecibidos = recibida * (it.presentacionLitros || 0);
      let status = 'ok'; // recibida === solicitada
      if (recibida < solicitada) status = 'less';
      if (recibida > solicitada) status = 'more';
      if (recibida === 0) status = 'zero';
      return { ...it, recibida, litrosRecibidos, status };
    });
  }, [receivedItems]);

  const totalLitrosRecibidos = useMemo(() => itemsWithStatus.reduce((sum, it) => sum + it.litrosRecibidos, 0), [itemsWithStatus]);
  const totalLitrosSolicitados = useMemo(() => itemsWithStatus.reduce((sum, it) => sum + (it.litrosSolicitados || 0), 0), [itemsWithStatus]);

  const validItems = useMemo(() => itemsWithStatus.filter(it => it.recibida > 0), [itemsWithStatus]);
  const hasDiscrepancies = itemsWithStatus.some(it => it.status === 'less' || it.status === 'more');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validItems.length === 0) {
      toast({ title: 'Sin productos', description: 'Ingresa al menos una cantidad recibida mayor a 0.', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        orderId: order?.id || order?._id,
        items: validItems.map(it => ({
          productoId: it.productoId,
          cantidadRecibida: it.recibida,
          costoUnitario: Number(it.costoUnitario) || undefined,
        })),
        fecha: new Date(fecha).toISOString(),
        numeroFactura: numeroFactura || undefined,
        observaciones: observaciones || undefined,
      };
      const res = await receiveOrder(payload);
      toast({
        title: 'Pedido recibido',
        description: res?.message || `${validItems.length} producto(s) ingresados al inventario.`,
      });
      onOpenChange(false);
      onReceived && onReceived(res);
    } catch (error) {
      console.error('Error recibiendo pedido:', error);
      let message = 'No se pudo recibir el pedido.';
      try {
        const parsed = JSON.parse(error.message);
        message = parsed?.message || message;
      } catch {
        message = error.message || message;
      }
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-green-600" />
            Recibir pedido {order?.numeroPedido || ''}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto space-y-5 pr-2">
            {/* Info del pedido */}
            <div className="rounded-lg border border-border p-3 bg-muted/30">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Pedido</p>
                  <p className="font-semibold text-foreground">{order?.numeroPedido || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Proveedor</p>
                  <p className="font-semibold text-foreground">{order?.proveedor || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Fecha pedido</p>
                  <p className="font-semibold text-foreground">
                    {order?.fecha ? new Date(order.fecha).toLocaleDateString('es-CO') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Productos</p>
                  <p className="font-semibold text-foreground">{order?.items?.length || 0}</p>
                </div>
              </div>
            </div>

            {/* Fecha recepción y factura */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Numero de factura</label>
                <Input type="text" placeholder="Opcional" value={numeroFactura} onChange={(e) => setNumeroFactura(e.target.value)} />
              </div>
              <div className="sm:col-start-3">
                <label className="block text-sm font-medium text-foreground mb-1">Fecha de recepcion</label>
                <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
              </div>
            </div>

            {/* Tabla de verificación de cantidades */}
            <div>
              <p className="text-sm font-medium text-foreground mb-2">Verificar cantidades recibidas</p>
              <div className="rounded-md border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium text-muted-foreground">Producto</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Cant. solic.</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Litros solic.</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground w-[120px]">Cant. recibida</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground">Litros a recibir</th>
                      <th className="text-right px-3 py-2 font-medium text-muted-foreground w-[120px]">Costo/L</th>
                      <th className="text-center px-3 py-2 font-medium text-muted-foreground w-[70px]">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsWithStatus.map((it, idx) => (
                      <tr key={idx} className="border-b last:border-b-0">
                        <td className="px-3 py-2 text-foreground">{it.productoNombre}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{it.cantidadSolicitada}</td>
                        <td className="px-3 py-2 text-right text-muted-foreground">{it.litrosSolicitados.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            type="number"
                            value={it.cantidadRecibida}
                            onChange={(e) => updateReceivedQty(idx, e.target.value)}
                            min="0"
                            step="0.01"
                            className="h-8 w-[100px] ml-auto text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-foreground">{it.litrosRecibidos.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">
                          <Input
                            type="number"
                            value={it.costoUnitario}
                            onChange={(e) => updateCosto(idx, e.target.value)}
                            min="0"
                            step="0.01"
                            className="h-8 w-[100px] ml-auto text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          {it.status === 'ok' && (
                            <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                          )}
                          {it.status === 'less' && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                              <AlertTriangle className="h-3.5 w-3.5" /> -{it.cantidadSolicitada - it.recibida}
                            </span>
                          )}
                          {it.status === 'more' && (
                            <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                              <AlertTriangle className="h-3.5 w-3.5" /> +{it.recibida - it.cantidadSolicitada}
                            </span>
                          )}
                          {it.status === 'zero' && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30 font-medium">
                      <td className="px-3 py-2 text-foreground">Total</td>
                      <td className="px-3 py-2 text-right text-muted-foreground"></td>
                      <td className="px-3 py-2 text-right text-muted-foreground">{totalLitrosSolicitados.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right"></td>
                      <td className="px-3 py-2 text-right text-foreground">{totalLitrosRecibidos.toLocaleString()}</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Alerta de discrepancias */}
              {hasDiscrepancies && (
                <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    Hay diferencias entre las cantidades solicitadas y recibidas. Verifica antes de confirmar.
                  </p>
                </div>
              )}
            </div>

            {/* Observaciones */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Observaciones</label>
              <textarea
                value={observaciones}
                onChange={(e) => setObservaciones(e.target.value)}
                placeholder="Opcional"
                className="w-full border border-input rounded-md p-2 min-h-[60px] bg-background text-foreground focus:border-ring focus:ring-1 focus:ring-ring"
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting || validItems.length === 0}>
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Procesando...</>
              ) : (
                <><PackageCheck className="h-4 w-4 mr-2" />Confirmar recepcion ({validItems.length} producto{validItems.length !== 1 ? 's' : ''})</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReceiveOrderModal;
